import * as React from 'react';
import { Redirect, Route, RouteProps } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { omit, isEmpty, pick, includes } from 'lodash-es';
import { parse, stringify } from 'query-string';

import { ApplicationState as AppState, getRouterStore, getConfigStore } from '@api/store';
import Authentication from '../../Authentication';
import { AccountState } from '@api/store/account/types';
import { AccessLevel, AccountOptions, ProductCode, ProductType } from '@dev/ee-api/ee-api-models';
import DragDropEditor from '@api/components/EditorDragDrop';
import { SessionFailedAuth } from '@api/store/session/actions';
import { FeatureUpselling } from '@api/components/FeatureUpselling';
import { useUpselling } from '@api/services/hooks/useUpselling';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { URLSearchParamsEnum } from '@api/services/dictionaries/UrlSearchParams';
import { DashboardPaths, HomePaths } from '@api/services/dictionaries/paths';
import { OnboardingSurvey } from '@api/components/Surveys/OnboardingSurvey';
import { isAccountActivated } from '@api/services/Security';

interface PrivateRouteInterface extends RouteProps {
    accessLevel: AccessLevel[];
    account?: AccountState;
    disableBanners?: boolean;
    location: any;
    upsellingFeature?: AvailableFeaturesUpselling;
}
const doNotSetReturnUrlOnThose = ['/login', '/create-account'];

const mapState = (state: AppState, ownProps: PrivateRouteInterface): PrivateRouteInterface => {
    return {
        ...ownProps,
        account: state.account,
    };
};

const useParseQueryParams = () => {
    const { location } = useSelector(getRouterStore);
    const queryParams = parse(location.search);
    const parsedQueryParams = omit(queryParams, ['t', 'message']);
    let returnUrl = location.pathname;
    let token = '';
    if (!isEmpty(parsedQueryParams)) {
        returnUrl += `?${stringify(parsedQueryParams)}`;
    }
    if (queryParams.t) {
        token = stringify(pick(queryParams, ['t', 'i']));
    }

    return {
        returnUrl,
        token,
        urlParams: stringify(omit(queryParams, ['t'])),
    };
};

const changeDashboardPaths = (url: string, productCode: ProductCode | ProductType) => {
    if (location.href.includes(URLSearchParamsEnum.ShareIdentifier)) {
        return url.replace(`${DashboardPaths.EmailApi}`, DashboardPaths?.[productCode]);
    }

    return DashboardPaths[productCode];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const PrivateRoute: React.FC<PrivateRouteInterface> = (params: PrivateRouteInterface): JSX.Element => {
    const { returnUrl, token, urlParams } = useParseQueryParams();
    const { bootedAsChild } = useSelector(getConfigStore);
    const { isUpsellingVisible } = useUpselling();
    const dispatch = useDispatch();

    if (!Authentication.isAuthenticated()) {
        const to = {
            pathname: '/login',
            search: token || urlParams,
        };
        if (!doNotSetReturnUrlOnThose.includes(window.location.pathname)) {
            sessionStorage.setItem('returnUrl', returnUrl);
        }
        dispatch(SessionFailedAuth());
        return <Redirect to={to} />;
    }

    const { account, location } = params;

    if (!account.accountLoading && account?.GeneralInfo) {
        const { AccountAccessLevel, Options } = account.GeneralInfo;
        const accountHasAccessLevel = params.accessLevel?.every((singleAccessLevel) => AccountAccessLevel.includes(singleAccessLevel));
        const isAccountActive = isAccountActivated();

        if (!isAccountActive) {
            return <Redirect to={HomePaths.ConfirmEmail} />;
        }

        const requiredOnboardingSurvey = includes(Options, AccountOptions.RequiredOnboardingSurvey);

        if (requiredOnboardingSurvey) {
            return <Route component={OnboardingSurvey} />;
        }

        const canSeeUpselling = params.upsellingFeature && isUpsellingVisible && !accountHasAccessLevel;

        if (canSeeUpselling) {
            const routeParams = omit(params, 'component');
            return <Route {...routeParams} render={() => <FeatureUpselling feature={params.upsellingFeature} />} />;
        }

        if (!accountHasAccessLevel) {
            return <Redirect to="/401" />;
        }
    }

    if (!account.Profile) {
        return <></>;
    }

    if (bootedAsChild && account.GeneralInfo.ProductType !== ProductType.API) {
        window.location.pathname = changeDashboardPaths(location.pathname, account.GeneralInfo.ProductType);
        return <></>;
    }

    const linksToLoadComponent = ['#/templates/edit', '#/templates/create', '#/landingpages/edit', '#/landingpages/create'];

    if (linksToLoadComponent.some((link) => location.hash.includes(link))) {
        return <Route {...params} component={DragDropEditor} />;
    }

    const Component = params.component;

    return (
        <>
            <Route {...params} component={Component} />
        </>
    );
};

export default connect(mapState)(PrivateRoute);
